import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 340px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #34574A;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ActionButton = styled.button`
  background: #34574A;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #27443a;
  }
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatusButton = styled.button`
  background: ${props => props.$active ? '#22c55e' : '#c62828'};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  opacity: ${props => props.$active ? 1 : 0.6};
  &:hover {
    opacity: 0.85;
  }
`;

const CategoriaForm = ({ onSubmit, onCancel, initialData }) => {
  const [form, setForm] = useState({
    nomeCATEGORIA: initialData?.nomeCATEGORIA || '',
    slugCATEGORIA: initialData?.slugCATEGORIA || '',
    tipoCATEGORIA: initialData?.tipoCATEGORIA ? String(initialData.tipoCATEGORIA) : '',
    ordemCATEGORIA: initialData?.ordemCATEGORIA ?? '',
    imagemCATEGORIA: initialData?.imagemCATEGORIA || '', // URL já salva no backend (para edição)
    destaqueCATEGORIA: initialData?.destaqueCATEGORIA ?? 0,
    ativoCATEGORIA: initialData?.ativoCATEGORIA === 0 ? 0 : 1,
  });
  const [tipos, setTipos] = useState([]);
  const [arquivoImagem, setArquivoImagem] = useState(null);

  // Função para obter imagem padrão baseada no slug
  const getImagemPadrao = (slug) => {
    if (!slug) return '';
    
    const slugLower = String(slug).toLowerCase();
    const imagensPadrao = {
      'apartamento': '/tipos/apartamento.png',
      'apartamentos': '/tipos/apartamento.png',
      'castelo': '/tipos/castelo.png',
      'castelos': '/tipos/castelo.png',
      'palacios-e-castelos': '/tipos/castelo.png',
      'palacios': '/tipos/castelo.png',
      'condominio': '/tipos/condominio.png',
      'condominios': '/tipos/condominio.png',
      'hotel': '/tipos/praiaPortugal.png',
      'casa': '/tipos/casa.png',
      'studio': '/tipos/studio.png',
      'moradia': '/tipos/casa.png',
      'predio': '/tipos/apartamento.png',
    };
    
    return imagensPadrao[slugLower] || '';
  };

  // Recarrega formulário ao abrir para edição ou novo
  useEffect(() => {
    const imagemBanco = initialData?.imagemCATEGORIA || '';
    const imagemPadrao = getImagemPadrao(initialData?.slugCATEGORIA);
    const imagemFinal = imagemBanco || imagemPadrao;
    
    setForm({
      nomeCATEGORIA: initialData?.nomeCATEGORIA || '',
      slugCATEGORIA: initialData?.slugCATEGORIA || '',
      tipoCATEGORIA: initialData?.tipoCATEGORIA ? String(initialData.tipoCATEGORIA) : '',
      ordemCATEGORIA: initialData?.ordemCATEGORIA ?? '',
      imagemCATEGORIA: imagemFinal,
      destaqueCATEGORIA: initialData?.destaqueCATEGORIA ?? 0,
      ativoCATEGORIA: initialData?.ativoCATEGORIA === 0 ? 0 : 1,
    });
    setArquivoImagem(null);
  }, [initialData]);

  // Função para gerar slug
  function slugify(text) {
    return text
      .toString()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .toLowerCase();
  }

  useEffect(() => {
    fetch(`${import.meta.env.VITE_URL_API}/tiposimovel`, { headers: { 'accept': 'application/json' } })
      .then(res => res.json())
      .then(data => setTipos(data))
      .catch(() => setTipos([]));
  }, []);

  // Seleciona o primeiro tipo por padrão se nada estiver escolhido
  useEffect(() => {
    if (form.tipoCATEGORIA || !Array.isArray(tipos) || !tipos.length) return;
    const first = tipos[0];
    if (first?.codigoTIPOIMOVEL != null) {
      setForm(prev => ({ ...prev, tipoCATEGORIA: String(first.codigoTIPOIMOVEL) }));
    }
  }, [tipos, form.tipoCATEGORIA]);

  useEffect(() => {
    setForm(prev => ({
      ...prev,
      slugCATEGORIA: slugify(prev.nomeCATEGORIA || ''),
    }));
  }, [form.nomeCATEGORIA]);

  const handleSubmit = e => {
    e.preventDefault();
    // Log completo para depuração do payload antes do envio
    console.log('[CategoriaForm] submit', {
      nome: form.nomeCATEGORIA,
      slug: form.slugCATEGORIA,
      tipo: form.tipoCATEGORIA,
      ativo: form.ativoCATEGORIA,
      temArquivoImagem: !!arquivoImagem,
      nomeArquivoImagem: arquivoImagem?.name,
      tamanhoArquivoImagem: arquivoImagem?.size,
      imagemCATEGORIA: form.imagemCATEGORIA,
      initialDataImagem: initialData?.imagemCATEGORIA
    });
    onSubmit({ ...form, arquivoImagem });
  };

  return (
    <FormContainer onSubmit={handleSubmit}>
      <Field>
        <Label>Nome</Label>
        <Input
          value={form.nomeCATEGORIA}
          onChange={e => setForm({ ...form, nomeCATEGORIA: e.target.value })}
          required
        />
      </Field>
      <Field>
        <Label>Slug</Label>
        <Input
          value={form.slugCATEGORIA}
          onChange={e => setForm({ ...form, slugCATEGORIA: slugify(e.target.value) })}
          required
          readOnly
        />
      </Field>
      <Field>
        <Label>Tipo</Label>
        <Select
          value={form.tipoCATEGORIA}
          onChange={e => setForm({ ...form, tipoCATEGORIA: e.target.value })}
          required
        >
          <option value="">Selecione o tipo</option>
          {tipos.map((t) => (
            <option key={t.codigoTIPOIMOVEL} value={String(t.codigoTIPOIMOVEL)}>{t.nomeTIPOIMOVEL}</option>
          ))}
        </Select>
      </Field>
      <Field>
        <Label>Ordem na home</Label>
        <Input
          type="number"
          value={form.ordemCATEGORIA}
          onChange={e => setForm({
            ...form,
            ordemCATEGORIA: e.target.value === '' ? '' : Number(e.target.value),
          })}
          placeholder="Posição do card: 1, 2, 3..."
        />
      </Field>
      {/* Exibir imagem atual (padrão ou personalizada) se existir */}
      {form.imagemCATEGORIA && !arquivoImagem && (
        <Field>
          <Label>
            {initialData?.imagemCATEGORIA ? 'Imagem atual' : 'Imagem padrão'}
          </Label>
          <div style={{ position: 'relative' }}>
            <img
              src={form.imagemCATEGORIA}
              alt="Imagem da categoria"
              style={{
                width: '100%',
                maxHeight: 180,
                objectFit: 'cover',
                borderRadius: 8,
                border: initialData?.imagemCATEGORIA ? '1px solid #eee' : '2px solid #34574A',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            {!initialData?.imagemCATEGORIA && (
              <small style={{ 
                color: '#34574A', 
                fontSize: '0.8rem', 
                marginTop: '4px', 
                display: 'block',
                fontStyle: 'italic'
              }}>
                Esta é a imagem padrão para esta categoria
              </small>
            )}
          </div>
        </Field>
      )}
      
      <Field>
        <Label>
          {form.imagemCATEGORIA ? 'Trocar imagem' : 'Upload de Imagem'}
        </Label>
        <Input
          type="file"
          accept="image/*"
          onChange={e => {
            const file = e.target.files?.[0] || null;
            setArquivoImagem(file);
          }}
        />
        {form.imagemCATEGORIA && (
          <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
            {initialData?.imagemCATEGORIA 
              ? 'Selecione um novo arquivo apenas se desejar substituir a imagem atual'
              : 'Selecione um arquivo para substituir a imagem padrão por uma imagem personalizada'
            }
          </small>
        )}
      </Field>
      
      {/* Pré-visualização do novo arquivo selecionado */}
      {arquivoImagem && (
        <Field>
          <Label>Nova imagem (pré-visualização)</Label>
          <img
            src={URL.createObjectURL(arquivoImagem)}
            alt="Pré-visualização da nova imagem"
            style={{
              width: '100%',
              maxHeight: 180,
              objectFit: 'cover',
              borderRadius: 8,
              border: '2px solid #34574A',
            }}
          />
        </Field>
      )}
      <Field>
        <Label>Exibir na home</Label>
        <Select
          value={form.destaqueCATEGORIA}
          onChange={e => setForm({ ...form, destaqueCATEGORIA: Number(e.target.value) })}
        >
          <option value={0}>Não</option>
          <option value={1}>Sim</option>
        </Select>
        <small style={{ color: '#666', fontSize: '0.85rem' }}>
          Categorias com "Sim" aparecem como cards na página inicial, na ordem definida acima.
          Para trocar um card, desmarque a categoria atual e marque a nova com a mesma posição.
        </small>
      </Field>
      <Field>
        <Label>Status</Label>
        <StatusRow>
          <StatusButton
            type="button"
            $active={form.ativoCATEGORIA === 1}
            onClick={() => setForm({ ...form, ativoCATEGORIA: 1 })}
          >Ativo</StatusButton>
          <StatusButton
            type="button"
            $active={form.ativoCATEGORIA === 0}
            onClick={() => setForm({ ...form, ativoCATEGORIA: 0 })}
          >Inativo</StatusButton>
        </StatusRow>
      </Field>
      <ButtonRow>
        <ActionButton type="button" style={{background:'#bbb',color:'#222'}} onClick={onCancel}>Cancelar</ActionButton>
        <ActionButton type="submit">Salvar</ActionButton>
      </ButtonRow>
    </FormContainer>
  );
};

export default CategoriaForm;
